import type { OperationStatus } from './apis/apiResponse.model';
import type {
  OnstarConnectionStatus,
  OnstarSupportStatus,
  OrganizationOwnershipStatus,
  ProvisioningStatus,
} from './asset.model';
import type { ConnectivityStatusType } from './common.model';

export interface ConnectivityStatusConfig {
  name: string;
  type: ConnectivityStatusType;
  count: number;
  description: string;
  color: string;
}

export interface ConnectivityChartConfig {
  labels: string[];
  data: number[];
  colors: string[];
  total: number;
}

export interface ConnectivityChartTooltip {
  opacity: number;
  top: number;
  left: number;
  title: string;
  values: Array<string>;
  colors: Array<string>;
}

export interface ApiConnectivityStatusSummary {
  organizationId: string;
  totalVehicleCount: number;
  items: {
    organizationPrimaryOwnershipStatus?: OrganizationOwnershipStatus | null;
    provisioningState?: ProvisioningStatus | null;
    onStarTcpsAccepted?: boolean | null;
    connectionStatus?: OnstarConnectionStatus | null;
    onStarSupportStatus?: OnstarSupportStatus | null;
  }[];
}

/** V20260602 vehicle status dashboard summary with per-status counts (new connectivity flag). */
export type ApiVehicleStatusSummaryV20260602 = {
  verificationPending?: number;
  notEquipped?: number;
  connectionPending?: number;
  connected?: number;
};

export interface ConnectivityStatusSummary {
  organizationId: string;
  totalVehicleCount: number;
  connectivityStatuses: {
    verificationPending: number;
    notEquipped?: number; // Populated when NOT_EQUIPPED feature flag is enabled
    verified?: number; // Populated when NOT_EQUIPPED feature flag is disabled
    connectionPending: number;
    connected: number;
  };
}

export interface CSDashboardSummary {
  summaries: DashboardSessionConfig;
}

export interface DashboardSummarySession {
  connectivityStatusSummary?: ConnectivityStatusSummary;
  selectedConnectivityStatus?: ConnectivityStatusType;
  diagnosticsSummary?: DiagnosticSummary;
  recallSummary?: RecallSummary;
  oilLifeSummary?: OilLifeSummary;
  chargeLevelSummary?: ChargeLevelSummary;
}

export enum DashboardSessionViewType {
  MAIN = 'Main Dashboard View',
}

export type DashboardSessionConfig = {
  [key: string]: Partial<DashboardSummarySession>;
};

export enum DashboardRequestType {
  GET_DASHBOARD_CONNECTIVITY_STATUS_SUMMARY = 'Get Dashboard Connectivity Status Summary',
  GET_DASHBOARD_DIAGNOSTICS_SUMMARY = 'Get Dashboard Diagnostics Summary',
  GET_DASHBOARD_RECALL_SUMMARY = 'Get Dashboard Recall Summary',
  GET_DASHBOARD_OIL_LIFE_SUMMARY = 'Get Dashboard Oil Life Summary',
}

export interface CSDashboardState {
  sessionConfigs: DashboardSessionConfig;
  operations: {
    [key in DashboardRequestType]?: OperationStatus;
  };
}

export interface DiagnosticsCount {
  criticalCount: number;
  warningCount: number;
  okCount: number;
  totalCount: number;
}
export interface DiagnosticSummary {
  organizationId: string;
  totalVehicleCount: number;
  diagnostics: DiagnosticsCount;
  fleetId?: string;
}

export interface DiagnosticChartTooltip {
  opacity: number;
  top: number;
  left: number;
  title: string;
  values: Array<string>;
  colors: Array<string>;
}

export interface RecallSummary {
  organizationId: string;
  activeFieldActionVehicleCount: number | null;
}

export interface OilLifeSummary {
  organizationId: string;
  fleetId: string;
  totalIceCount: number;
  oilLifeStatusSummary: {
    criticalCount: number;
    lowCount: number;
    goodCount: number;
  };
}
/** @deprecated */
export interface ChargeLevelSummary {
  organizationId: string;
  fleetId: string;
  totalEVCount: number;
  chargeLevelSummary: {
    charging: number;
    criticalCount: number;
    lowCount: number;
    goodCount: number;
    highCount: number;
  };
}
export interface WidgetProps {
  displayMockData?: boolean;
  hasConnectedVehicles?: boolean;
}

/** V20260602 vehicle status summary query state passed from the dashboard view. */
export type NewVehicleStatusSummaryQuery = {
  summary?: ConnectivityStatusSummary;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
};

export type PendingVehiclesProps = WidgetProps & {
  newVehicleStatusSummaryQuery?: NewVehicleStatusSummaryQuery;
};
export interface ChargeChartTooltip {
  opacity: number;
  top: number;
  left: number;
  title: string;
  values: Array<string>;
  colors: Array<string>;
}
